// absensi.repo — shift, clock-in/out, riwayat, rekap.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import type { SesiIstirahat } from '@domain/attendance';

export interface Shift { id: number; outlet: string | null; nama: string; jam_masuk: string; jam_keluar: string; toleransi: number }
export interface KorrEntry { action: string; by: string; role: string; ket: string; from: string; to: string; at: string; foto?: string | null }
export interface AttRow { id: number; emp_id: number; outlet: string | null; tgl: string; shift_id: number | null; masuk_at: string | null; keluar_at: string | null; kategori: string | null; ket: string | null; kat_override: string | null; req_status: string | null; req_reason: string | null; korr_log: KorrEntry[] | null; selfie: string | null; istirahat?: SesiIstirahat[] | null }

export async function listShifts(outlet?: string | null): Promise<Shift[]> {
  let q = supabase().from('shifts').select('id,outlet,nama,jam_masuk,jam_keluar,toleransi').order('outlet').order('jam_masuk');
  if (outlet) q = q.eq('outlet', outlet);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as Shift[];
}
export interface ShiftInput { outlet: string | null; nama: string; jam_masuk: string; jam_keluar: string; toleransi: number }
export async function createShift(d: ShiftInput): Promise<void> {
  const { error } = await supabase().from('shifts').insert(d); if (error) throw new Error(error.message);
}
export async function updateShift(id: number, d: ShiftInput): Promise<void> {
  const { error } = await supabase().from('shifts').update(d).eq('id', id); if (error) throw new Error(error.message);
}
export async function deleteShift(id: number): Promise<void> {
  const { error } = await supabase().from('shifts').delete().eq('id', id); if (error) throw new Error(error.message);
}
export async function todayAttendance(empId: number, tgl: string): Promise<AttRow | null> {
  const { data, error } = await supabase().from('attendance').select('*').eq('emp_id', empId).eq('tgl', tgl).limit(1);
  if (error) throw new Error(error.message);
  return ((data as AttRow[] | null)?.[0]) ?? null;
}
export interface ClockInRow {
  emp_id: number; outlet: string | null; tgl: string; shift_id: number; masuk_at: string; kategori: string; ket: string;
  masuk_lat?: number | null; masuk_lng?: number | null; in_radius?: boolean | null; req_status?: string | null; req_reason?: string | null;
  selfie?: string | null; // path objek bucket 'absensi' (0054); folder = emp_id
}
export async function clockIn(d: ClockInRow): Promise<void> {
  const { error } = await supabase().from('attendance').insert(d);
  if (!error) return;
  // Fallback pre-0041: kolom geo (masuk_lat/lng/in_radius) belum ada → insert kolom dasar saja (clock-in tetap jalan).
  if (/column .*(masuk_lat|masuk_lng|in_radius).* does not exist/i.test(error.message)) {
    const { masuk_lat: _a, masuk_lng: _b, in_radius: _c, ...base } = d;
    const { error: e2 } = await supabase().from('attendance').insert(base);
    if (e2) throw new Error(e2.message);
    return;
  }
  throw new Error(error.message);
}

// ---------- Device-binding (migrasi 0041, tabel employee_devices) ----------
export interface DeviceRow { emp_id: number; token: string; fp: string; registered_at: string }
export async function getDevice(empId: number): Promise<DeviceRow | null> {
  const { data, error } = await supabase().from('employee_devices').select('*').eq('emp_id', empId).maybeSingle();
  if (error) throw new Error(error.message);
  return (data as DeviceRow | null) ?? null;
}
export async function registerDevice(empId: number, token: string, fp: string): Promise<void> {
  const { error } = await supabase().from('employee_devices').insert({ emp_id: empId, token, fp });
  if (error) throw new Error(error.message);
}
export async function resetDevice(empId: number): Promise<void> {
  const { error } = await supabase().from('employee_devices').delete().eq('emp_id', empId);
  if (error) throw new Error(error.message);
}
/** emp_id yang sudah terikat device (untuk badge di UI reset). */
export async function boundDeviceIds(): Promise<number[]> {
  const { data, error } = await supabase().from('employee_devices').select('emp_id');
  if (error) throw new Error(error.message);
  return ((data ?? []) as { emp_id: number }[]).map((r) => r.emp_id);
}
export async function clockOut(empId: number, tgl: string, at: string): Promise<void> {
  const { error } = await supabase().from('attendance').update({ keluar_at: at }).eq('emp_id', empId).eq('tgl', tgl);
  if (error) throw new Error(error.message);
}
/** Simpan daftar sesi istirahat hari itu. RLS self-write menjaga hanya baris sendiri yang bisa diubah. */
export async function simpanIstirahat(id: number, sesi: SesiIstirahat[]): Promise<void> {
  const { error } = await supabase().from('attendance').update({ istirahat: sesi }).eq('id', id);
  if (error) throw new Error(error.message);
}
export async function myAttendance(empId: number, fromTgl: string): Promise<AttRow[]> {
  const { data, error } = await supabase().from('attendance').select('*').eq('emp_id', empId).gte('tgl', fromTgl).order('tgl', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as AttRow[];
}
// ---------- Koreksi kehadiran (migrasi 0039) ----------
/** Karyawan mengajukan koreksi pada absensi SENDIRI (RLS self-write). */
export async function requestKoreksi(id: number, reason: string, korrLog: KorrEntry[]): Promise<void> {
  const { error } = await supabase().from('attendance').update({ req_status: 'pending', req_reason: reason, korr_log: korrLog }).eq('id', id);
  if (error) throw new Error(error.message);
}
/** Approver menyimpan hasil koreksi (kat_override + req_status + korr_log). RLS+trigger backstop. */
export async function saveKoreksi(id: number, patch: { kat_override: string | null; req_status: string | null; korr_log: KorrEntry[] }): Promise<void> {
  const { error } = await supabase().from('attendance').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}
/** Ambil satu absensi + peran karyawannya (untuk guard approver di service). */
export async function getAttFull(id: number): Promise<(AttRow & { role: string }) | null> {
  const { data, error } = await supabase().from('attendance').select('*,employees(role)').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const d = data as unknown as AttRow & { employees: { role: string } | { role: string }[] | null };
  return { ...d, role: (Array.isArray(d.employees) ? d.employees[0]?.role : d.employees?.role) ?? '' };
}

/** Semua absensi (RLS scope) + nama karyawan + jam masuk/keluar shift (pulang-cepat/menit-telat) — utk rekap. */
export interface RekapRow extends AttRow { nama: string; role: string; shiftKeluar: string | null; shiftMasuk: string | null }
type Rel = { nama: string; role: string };
type ShiftJam = { jam_keluar: string; jam_masuk: string };
const one = <T,>(r: T | T[] | null): T | null => (Array.isArray(r) ? r[0] ?? null : r);
/** Koreksi kehadiran PENDING (+identitas) — hub 🛡️ Approval. RLS attendance membatasi scope. */
export interface KorrPending { id: number; tgl: string; req_reason: string | null; kategori: string | null; emp_id: number; nama: string; role: string; outlet: string | null }
export async function listKorrPending(): Promise<KorrPending[]> {
  const data = await fetchAllPaged<unknown>((a, b) => supabase().from('attendance')
    .select('id,tgl,req_reason,kategori,emp_id,employees(nama,role,outlet)')
    .eq('req_status', 'pending').order('tgl', { ascending: false }).order('id').range(a, b));
  return (data as unknown as (AttRow & { employees: { nama: string; role: string; outlet: string | null } | null })[])
    .map((r) => ({ id: r.id, tgl: r.tgl, req_reason: r.req_reason, kategori: r.kategori, emp_id: r.emp_id,
      nama: r.employees?.nama ?? '—', role: r.employees?.role ?? '—', outlet: r.employees?.outlet ?? null }));
}

export async function listAttendance(): Promise<RekapRow[]> {
  // Paging: absensi ~260 baris/bln → tembus 1.000 dalam ~4 bulan; tanpa ini KPI/Rekap historis
  // salah diam-diam (baris terlama hilang duluan karena urut desc).
  const data = await fetchAllPaged<AttRow & { employees: Rel | Rel[] | null; shifts: ShiftJam | ShiftJam[] | null }>((a, b) =>
    supabase().from('attendance').select('*,employees(nama,role),shifts(jam_keluar,jam_masuk)').order('tgl', { ascending: false }).order('id').range(a, b) as unknown as PromiseLike<{ data: (AttRow & { employees: Rel | Rel[] | null; shifts: ShiftJam | ShiftJam[] | null })[] | null; error: { message: string } | null }>);
  return data.map((r) => {
    const e = one(r.employees); const s = one(r.shifts);
    return { ...r, nama: e?.nama ?? '—', role: e?.role ?? '', shiftKeluar: s?.jam_keluar ?? null, shiftMasuk: s?.jam_masuk ?? null };
  });
}

// ---------- Cuti / Leave ----------
export interface Leave { id: number; emp_id: number; outlet: string | null; from_tgl: string; to_tgl: string; note: string | null; status: string; created_at: string; decided_by: string | null; decided_at: string | null; nama: string; role: string }
export async function createLeave(d: { emp_id: number; outlet: string | null; from_tgl: string; to_tgl: string; note: string }): Promise<void> {
  const { error } = await supabase().from('leaves').insert(d); if (error) throw new Error(error.message);
}
/** Cuti milik SATU karyawan (Absensi view — tak fetch cuti orang lain ke klien). */
export async function myLeaves(empId: number): Promise<Leave[]> {
  const { data, error } = await supabase().from('leaves').select('*,employees(nama,role)').eq('emp_id', empId).order('from_tgl', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as (Omit<Leave, 'nama' | 'role'> & { employees: Rel | Rel[] | null })[]).map((r) => { const e = one(r.employees); return { ...r, nama: e?.nama ?? '—', role: e?.role ?? '' }; });
}
export async function listLeaves(): Promise<Leave[]> {
  // Paging: cuti se-organisasi tumbuh; dipanggil tiap render KPI/Dashboard (fetchAbsCtx). Kalau
  // terpotong 1.000, cuti approved hilang → hari kerja efektif over-count → alpha palsu.
  const data = await fetchAllPaged<Omit<Leave, 'nama' | 'role'> & { employees: Rel | Rel[] | null }>((a, b) =>
    supabase().from('leaves').select('*,employees(nama,role)').order('from_tgl', { ascending: false }).order('id').range(a, b) as unknown as PromiseLike<{ data: (Omit<Leave, 'nama' | 'role'> & { employees: Rel | Rel[] | null })[] | null; error: { message: string } | null }>);
  return data.map((r) => {
    const e = one(r.employees);
    return { ...r, nama: e?.nama ?? '—', role: e?.role ?? '' };
  });
}
export async function decideLeave(id: number, status: 'approved' | 'rejected', by: string): Promise<void> {
  const { error } = await supabase().from('leaves').update({ status, decided_by: by, decided_at: new Date().toISOString() }).eq('id', id);
  if (error) throw new Error(error.message);
}
/** Ambil satu cuti + peran pengaju (untuk guard SPV-outlet di service). */
export async function getLeaveFull(id: number): Promise<(Leave) | null> {
  const { data, error } = await supabase().from('leaves').select('*,employees(nama,role)').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const r = data as unknown as (Omit<Leave, 'nama' | 'role'> & { employees: Rel | Rel[] | null });
  const e = one(r.employees);
  return { ...r, nama: e?.nama ?? '—', role: e?.role ?? '' };
}

// ---------- Geofence + hari kerja per CABANG (migrasi 0038) ----------
export interface OutletGeo { kode: string; nama: string; lat: number | null; lng: number | null; radius: number; hari_kerja: number[] }
export async function listOutletsGeo(): Promise<OutletGeo[]> {
  const { data, error } = await supabase().from('outlets').select('kode,nama,lat,lng,radius,hari_kerja').order('kode');
  if (error) throw new Error(error.message);
  return ((data ?? []) as Partial<OutletGeo>[]).map((o) => ({ kode: o.kode ?? '', nama: o.nama ?? '', lat: o.lat ?? null, lng: o.lng ?? null, radius: o.radius ?? 100, hari_kerja: o.hari_kerja ?? [1, 2, 3, 4, 5, 6] }));
}
export async function updateOutletGeo(kode: string, d: { lat: number | null; lng: number | null; radius: number; hari_kerja: number[] }): Promise<void> {
  const { error } = await supabase().from('outlets').update(d).eq('kode', kode); if (error) throw new Error(error.message);
}

// ---------- Hari Libur (migrasi 0038) ----------
export interface Holiday { id: number; tgl: string; nama: string; outlet: string | null }
export async function listHolidays(): Promise<Holiday[]> {
  const data = await fetchAllPaged<Holiday>((a, b) =>
    supabase().from('holidays').select('id,tgl,nama,outlet').order('tgl', { ascending: false }).order('id').range(a, b));
  return data as Holiday[];
}
export async function createHoliday(d: { tgl: string; nama: string; outlet: string | null }): Promise<void> {
  const { error } = await supabase().from('holidays').insert(d); if (error) throw new Error(error.message);
}
export async function deleteHoliday(id: number): Promise<void> {
  const { error } = await supabase().from('holidays').delete().eq('id', id); if (error) throw new Error(error.message);
}

// ---------- Keanggotaan cabang (pindah employees.outlet) ----------
export interface MemberEmp { id: number; nama: string; role: string; outlet: string | null }
// Owner/Manager = lintas-cabang (outlet null), Supplier = non-cabang → TAK dikelola keanggotaan cabang (port filter v0.18 outletEmpAdd).
export const CROSS_BRANCH_ROLES = ['Owner', 'Manager', 'Supplier'];
export async function listMembers(): Promise<MemberEmp[]> {
  const { data, error } = await supabase().from('employees').select('id,nama,role,outlet').eq('aktif', true)
    .not('role', 'in', `(${CROSS_BRANCH_ROLES.join(',')})`).order('nama');
  if (error) throw new Error(error.message);
  return (data ?? []) as MemberEmp[];
}
export async function setEmployeeOutlet(id: number, outlet: string | null): Promise<void> {
  const { error } = await supabase().from('employees').update({ outlet }).eq('id', id); if (error) throw new Error(error.message);
}
